import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PhoneMessenger from './PhoneMessenger';
import { threadLabel } from '../threadLabel';
const API = process.env.REACT_APP_API_URL || 'https://tcio-api.onrender.com';

const s = {
  wrap: { padding: '24px 28px', maxWidth: 1280 },
  h1: { fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 },
  bar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '14px 0 18px' },
  rangeBtn: (on) => ({ background: on ? '#336090' : '#fff', color: on ? '#fff' : '#334155', border: '1px solid ' + (on ? '#336090' : '#e2e8f0'), borderRadius: 8, padding: '6px 13px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', position: 'relative', height: 170, overflow: 'hidden' },
  title: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 800, letterSpacing: 0.4, marginBottom: 8 },
  big: { fontSize: 32, fontWeight: 800, color: '#0f172a' },
  sub: { fontSize: 12.5, color: '#64748b' },
  row: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f1f5f9' },
  ghost: { background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  flag: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 12.5, color: '#b91c1c', fontWeight: 700 },
};
const money = (n) => '$' + Math.round(n || 0).toLocaleString();
const dt = (d) => d ? new Date(String(d).slice(0,10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
const scoreColor = (v) => v == null ? '#94a3b8' : v >= 85 ? '#16a34a' : v >= 65 ? '#d97706' : '#dc2626';

const TITLES = {
  missed_calls: '🔴 Missed Calls', my_queue: 'My Queue', unclaimed_leads: 'Unclaimed Leads', log_referral: 'Log a Referral',
  team_admits_gauge: 'Team Admits', my_admits: 'My Admits', admits_today: 'Admits Today',
  pending_admits: 'Pending Admits', jts_admits: 'JTS Admits', aca_totals: 'AMA / ACA',
  leaderboard: 'Leaderboard', my_score: 'My TCIO Score', leads_action: 'Leads Needing Action',
  callbacks_today: 'My Callbacks Today', vobs_outstanding: 'VOBs Outstanding', lead_sources: 'Lead Sources',
  census_by_facility: 'Census by Facility', open_beds: 'Open Beds', discharges_upcoming: 'Discharges Next 7 Days',
  ur_due_today: 'UR \u2014 Due Today', ur_expiring: 'Auths Expiring \u2264 3 Days', collections_range: 'Collected', forecast_week: 'Forecast This Week',
  ai_review_pending: 'AI Calls to Review', tickets_open: 'My Open Tickets',
  prescreens_requested: '📋 Prescreens Requested',
  clinical_safety: 'Clinical & Safety', money_ar: 'Open AR',
  ur_expiring_dollars: 'Auth Revenue at Risk', staffing_today: 'Staffing Today',
  ready_to_bill: 'Served / Not Yet Billed',
  exec_brief: 'TCIO Executive Brief',
  messenger: 'Messages',
};

const LIST_WIDGETS = ['ur_due_today','pending_admits','vobs_outstanding','missed_calls','admits_today','leaderboard','callbacks_today','census_by_facility','open_beds','discharges_upcoming','ur_expiring','lead_sources','aca_totals','prescreens_requested','my_admits'];

function UrDueTodayTile() {
  const [f, setF] = useState(null);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const tok = localStorage.getItem('tcio_token');
        const r = await fetch(API + '/api/ur/due-today', { headers: { Authorization: 'Bearer ' + tok } });
        const j = await r.json();
        if (!dead && j.ok) setF(j);
      } catch (e) {}
    })();
    return () => { dead = true; };
  }, []);
  if (!f) return <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading...</div>;
  const S = f.summary || {};
  const dueToday = (f.data.review_due || []).filter(r => r.days_overdue <= 1).length + (S.new_admits || 0);
  const worst = (f.data.expiring || [])[0];
  return (<>
    <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
      <div><span style={{ fontSize: 24, fontWeight: 800, color: dueToday > 0 ? '#b45309' : '#16a34a' }}>{dueToday}</span><span style={{ fontSize: 11, color: '#94a3b8' }}> due today</span></div>
      <div><span style={{ fontSize: 18, fontWeight: 800, color: (S.expiring || 0) > 0 ? '#dc2626' : '#16a34a' }}>{S.expiring || 0}</span><span style={{ fontSize: 11, color: '#94a3b8' }}> lapsed</span></div>
      <div><span style={{ fontSize: 18, fontWeight: 800, color: (S.new_admits || 0) > 0 ? '#2563eb' : '#16a34a' }}>{S.new_admits || 0}</span><span style={{ fontSize: 11, color: '#94a3b8' }}> new admits</span></div>
    </div>
    {worst && <div style={{ fontSize: 12, marginTop: 6 }}><span style={{ fontWeight: 600 }}>{worst.patient_name}</span><span style={{ color: '#dc2626', fontWeight: 700 }}> {worst.days_uncovered}d uncovered</span></div>}
    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>tap to open UR</div>
  </>);
}

function SortableTile({ id, editMode, children, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !editMode });
  return (
    <div ref={setNodeRef} onClick={onClick}
      {...(editMode ? { ...attributes, ...listeners } : {})}
      style={{
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.65 : 1, zIndex: isDragging ? 10 : 'auto',
        cursor: editMode ? 'grab' : 'pointer', touchAction: editMode ? 'none' : 'auto',
      }}>
      {children}
    </div>
  );
}

function Widget({ k, d, editMode, onRemove, onMove, expanded, nav, callBack, dialBusy, user }) {
  const body = () => {
    if (!d) return <div style={s.sub}>No data</div>;
    if (!expanded && LIST_WIDGETS.includes(k)) {
      // COMPACT SUMMARY — click to expand
      switch (k) {
        case 'admits_today': return (<>
          <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
            <span style={{ fontSize: 24, fontWeight: 800 }}>{d.today.length}<span style={{ fontSize: 12, color: '#94a3b8' }}> today</span></span>
            <span style={{ fontSize: 24, fontWeight: 800, color: d.missed.length > 0 ? '#dc2626' : '#16a34a' }}>{d.missed.length}<span style={{ fontSize: 12, color: '#94a3b8' }}> missed</span></span>
          </div>
          {d.today.slice(0, 1).map(r => <div key={r.id} style={s.row}><span style={{ fontWeight: 600 }}>{r.patient_name}</span><span style={s.sub}>{r.facility}</span></div>)}
          {d.missed.slice(0, expanded ? 999 : 2).map(r => <div key={r.id} style={{ ...s.row, color: '#b91c1c', alignItems: 'center' }}>
          <span><span style={{ fontWeight: 700 }}>⚠ {r.patient_name}</span><span style={{ fontSize: 11.5, marginLeft: 6 }}>{r.days_late}d late · {r.facility}</span></span>
          {expanded && nav && <button onClick={(e) => { e.stopPropagation(); nav('bedboard'); }}
            style={{ background: '#336090', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 11px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>Open Bed Board</button>}
        </div>)}
          {d.missed.length > 2 && <div style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700, marginTop: 2 }}>+{d.missed.length - 2} more — tap</div>}
        </>);
        case 'leaderboard': return d.rows.length === 0 ? <div style={s.sub}>No scores yet</div> : (<>
          {d.rows.slice(0, 4).map(r => <div key={r.rank} style={{ ...s.row, padding: '3px 0' }}>
            <span style={{ fontWeight: r.rank === 1 ? 800 : 600, fontSize: 12.5 }}>{r.rank === 1 ? '🏆 ' : r.rank + '. '}{r.rep}</span>
            <b style={{ color: scoreColor(r.score), fontSize: 13 }}>{r.score}</b></div>)}
          {d.rows.length > 4 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>+{d.rows.length - 4} more — tap</div>}
        </>);
        case 'callbacks_today': return (<><div style={s.big}>{d.rows.length}</div><div style={s.sub}>scheduled today</div></>);
        case 'census_by_facility': {
          const tot = d.rows.reduce((n, r) => n + r.census, 0);
          return (<><div style={{ fontSize: 24, fontWeight: 800 }}>{tot}<span style={{ fontSize: 12, color: '#94a3b8' }}> total</span></div>
            {d.rows.slice(0, 4).map(r => <div key={r.facility} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>{r.facility}</span><b>{r.census}</b></div>)}
          </>);
        }
        case 'open_beds': {
          const tot = d.rows.reduce((n, r) => n + r.open, 0);
          return (<><div style={{ fontSize: 24, fontWeight: 800, color: tot <= 5 ? '#dc2626' : '#16a34a' }}>{tot}<span style={{ fontSize: 12, color: '#94a3b8' }}> open</span></div>
            {d.rows.slice(0, 4).map(r => <div key={r.facility} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>{r.facility}</span><b style={{ color: r.open <= 2 ? '#dc2626' : '#16a34a' }}>{r.open}</b></div>)}
          </>);
        }
        case 'discharges_upcoming': return (<>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{d.rows.length}<span style={{ fontSize: 12, color: '#94a3b8' }}> next 7 days</span></div>
          {d.rows.slice(0, 3).map((r, i) => <div key={i} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span style={{ fontWeight: 600 }}>{r.patient_name}</span><span style={s.sub}>{dt(r.expected_dc)}</span></div>)}
        </>);
        case 'ur_due_today': return <UrDueTodayTile />;
        case 'ur_expiring': return (<>
          <div style={{ fontSize: 24, fontWeight: 800, color: d.rows.length > 0 ? '#dc2626' : '#16a34a' }}>{d.rows.length}<span style={{ fontSize: 12, color: '#94a3b8' }}> expiring ≤3d</span></div>
          {d.rows.slice(0, 3).map((r, i) => <div key={i} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span style={{ fontWeight: 600 }}>{r.patient_name}</span><span style={{ color: '#dc2626', fontWeight: 700 }}>{dt(r.auth_thru)}</span></div>)}
        </>);
        case 'lead_sources': return d.rows.length === 0 ? <div style={s.sub}>—</div> : (<>
          {d.rows.slice(0, 4).map(r => <div key={r.source} style={{ ...s.row, padding: '3px 0', fontSize: 12.5 }}><span>{r.source}</span><b>{r.admits}</b></div>)}
          {d.rows.length > 4 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>+{d.rows.length - 4} more — tap</div>}
        </>);
        case 'aca_totals': return (<>
          <div style={{ fontSize: 24, fontWeight: 800, color: d.total > 0 ? '#dc2626' : '#16a34a' }}>{d.total}<span style={{ fontSize: 12, color: '#94a3b8' }}> AMA/ACA</span></div>
          {d.by_facility.slice(0, 4).map(r => <div key={r.facility} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>{r.facility}</span><b>{r.aca}</b></div>)}
          {d.by_facility.length === 0 && <div style={s.sub}>None in range 🎉</div>}
        </>);
        default: break;
      }
    }
    switch (k) {
      case 'messenger': {
        const th = (d && d.threads) || [];
        const unread = th.reduce((a, x) => a + Number(x.unread || 0), 0);
        return (<>
          <div style={{ ...s.big, color: unread > 0 ? '#0878E8' : '#0f172a' }}>{unread}</div>
          <div style={s.sub}>unread message{unread === 1 ? '' : 's'} - tap to open</div>
          {th.slice(0, 2).map(t => (
            <div key={t.id} style={s.row}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: Number(t.unread) > 0 ? 700 : 400 }}>
                {threadLabel(t)}</span>
              {Number(t.unread) > 0 && <span style={{ color: '#0878E8', fontWeight: 800 }}>{t.unread}</span>}
            </div>))}
        </>);
      }
      case 'exec_brief': return (<>
        {d.generating && !d.text ? (
          <div style={{ ...s.sub, padding: '6px 0' }}>Preparing your brief - check back in a moment.</div>
        ) : (<>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: '#1a2a3a', whiteSpace: 'pre-wrap' }}>{d.text}</div>
          <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 800, color: d.attention > 0 ? '#dc2626' : '#16a34a' }}>
            {d.attention > 0 ? `${d.attention} area${d.attention === 1 ? '' : 's'} need${d.attention === 1 ? 's' : ''} your attention` : 'No exceptions need your attention'}
          </div>
          {d.generated_at && <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>as of {new Date(d.generated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>}
        </>)}
      </>);
      case 'clinical_safety': return (<>
        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: d.criticals > 0 ? '#dc2626' : '#16a34a' }}>{d.criticals}<span style={{ fontSize: 12, color: '#94a3b8' }}> critical</span></span>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#d97706' }}>{d.warnings}<span style={{ fontSize: 11, color: '#94a3b8' }}> warn</span></span>
        </div>
        <div style={{ ...s.row, padding: '3px 0', fontSize: 12 }}><span>Patients w/ criticals</span><b style={{ color: d.critical_patients > 0 ? '#dc2626' : '#16a34a' }}>{d.critical_patients}</b></div>
        <div style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>Notes kicked back</span><b>{d.kicked_back}</b></div>
        <div style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>Pending cosign</span><b>{d.pending_cosign}</b></div>
      </>);
      case 'money_ar': return (<>
        <div style={{ fontSize: 24, fontWeight: 800 }}>${Math.round(Number(d.total_collectible)).toLocaleString()}<span style={{ fontSize: 12, color: '#94a3b8' }}> est. collectible</span></div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>${Math.round(Number(d.total_billed)).toLocaleString()} billed open</div>
        {d.by_facility.slice(0, 3).map(r => <div key={r.facility} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}><span>{r.facility}</span><b>${Math.round(Number(r.collectible)).toLocaleString()}</b></div>)}
      </>);
      case 'ur_expiring_dollars': return (<>
        <div style={{ fontSize: 24, fontWeight: 800, color: d.count > 0 ? '#dc2626' : '#16a34a' }}>${Math.round(Number(d.est_at_risk)).toLocaleString()}<span style={{ fontSize: 12, color: '#94a3b8' }}> a week at risk</span></div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{d.expiring_7d != null ? `${d.lapsed} lapsed, no renewal sent \u00b7 ${d.in_flight} renewal in flight \u00b7 ${d.expiring_7d} expiring in 7 days` : `${d.count} auths expiring within 7 days`}</div>
        {d.rows.slice().sort((x, y) => (({ lapsed: 0, expiring: 1, in_flight: 2 })[x.kind] ?? 1) - (({ lapsed: 0, expiring: 1, in_flight: 2 })[y.kind] ?? 1) || x.days_left - y.days_left).slice(0, 3).map((r, i) => <div key={i} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>{r.patient_name}</span>
          <span style={{ color: '#dc2626', fontWeight: 700 }}>{r.kind === 'lapsed' ? 'lapsed' : r.kind === 'in_flight' ? 'in flight' : r.days_left + 'd'}{r.est_week_exposure ? ' - $' + Math.round(r.est_week_exposure).toLocaleString() : ''}</span></div>)}
      </>);
      case 'ready_to_bill': return (<>
        <div style={{ fontSize: 24, fontWeight: 800, color: d.total_unbilled_days > 0 ? '#dc2626' : '#16a34a' }}>${Math.round(Number(d.total_billed_value)).toLocaleString()}<span style={{ fontSize: 12, color: '#94a3b8' }}> billed value</span></div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{d.total_unbilled_days} unbilled days - {d.patients} patients{d.leakage_patients > 0 ? ` - ${d.leakage_patients} likely leakage` : ''}</div>
        {(d.top || []).map((r, i) => <div key={i} style={{ ...s.row, padding: '2px 0', fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>{r.likely_leakage ? '\u26a0 ' : ''}{r.patient_name}<span style={{ color: '#94a3b8', fontSize: 11 }}> {r.loc}</span></span>
          <b style={{ color: '#dc2626' }}>{r.unbilled_past_due}d - ${Math.round(Number(r.billed_value)).toLocaleString()}</b></div>)}
      </>);
      case 'staffing_today': return (<>
        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
          <span style={{ fontSize: 24, fontWeight: 800 }}>{d.on_shift}<span style={{ fontSize: 12, color: '#94a3b8' }}> on shift</span></span>
          <span style={{ fontSize: 24, fontWeight: 800, color: d.coverage_gaps > 0 ? '#dc2626' : '#16a34a' }}>{d.coverage_gaps}<span style={{ fontSize: 12, color: '#94a3b8' }}> gaps</span></span>
        </div>
        <div style={s.sub}>today across scheduled houses</div>
      </>);
      case 'team_admits_gauge': {
        const p = Math.min(100, Math.round(100 * d.admits / d.goal));
        return (<><div style={{ ...s.big, color: d.admits >= d.goal ? '#16a34a' : '#0f172a' }}>{d.admits} <span style={{ fontSize: 16, color: '#94a3b8' }}>/ {d.goal}</span></div>
          <div style={{ background: '#f1f5f9', borderRadius: 6, height: 8, marginTop: 8 }}><div style={{ width: p + '%', background: d.admits >= d.goal ? '#16a34a' : '#336090', height: 8, borderRadius: 6 }} /></div></>);
      }
      case 'my_admits': return (<>
        <div style={{ fontSize: 24, fontWeight: 800 }}>{d.admits}<span style={{ fontSize: 12, color: '#94a3b8' }}> this range</span></div>
        {(d.rows || []).slice(0, expanded ? 999 : 3).map(r => <div key={r.id} style={{ ...s.row, padding: '3px 0', fontSize: 12.5 }}>
          <span style={{ fontWeight: 600 }}>{r.patient_name}</span>
          <span style={s.sub}>{r.facility || '—'} · {dt(r.admit_date)}</span>
        </div>)}
        {!expanded && (d.rows || []).length > 3 && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>+{d.rows.length - 3} more — tap</div>}
      </>);
      case 'my_score': return (<><div style={{ ...s.big, color: scoreColor(d.score) }}>{d.score ?? '—'}</div><div style={s.sub}>{d.admits} admits this month</div></>);
      case 'admits_today': return (<>
        {d.today.length === 0 && d.missed.length === 0 && <div style={s.sub}>No admits expected today.</div>}
        {d.today.map(r => <div key={r.id} style={s.row}><span style={{ fontWeight: 600 }}>{r.patient_name}</span><span style={s.sub}>{r.facility} · {r.loc || '—'}</span></div>)}
        {d.missed.map(r => <div key={r.id} style={s.flag}>⚠ {r.patient_name} — expected {dt(r.expected_admit_date)} ({r.days_late}d ago). Admit them or fix the date on the Bed Board.</div>)}
      </>);
      case 'prescreens_requested': return (<>
        <div style={{ fontSize: 24, fontWeight: 800, color: d.count > 0 ? '#d97706' : '#16a34a' }}>{d.count}<span style={{ fontSize: 12, color: '#94a3b8' }}> awaiting</span></div>
        {(d.items || []).slice(0, expanded ? 999 : 3).map(r => <div key={r.lead_id}
          onClick={(e) => { e.stopPropagation(); localStorage.setItem('tcio_prescreen_jump', r.lead_id); if (nav) nav('prescreen'); }}
          style={{ ...s.row, padding: expanded ? '6px 0' : '3px 0', fontSize: 12.5, cursor: 'pointer', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#6d28d9', textDecoration: 'underline' }}>{r.first_name} {r.last_name}</span>
          <span style={s.sub}>req {dt(r.prescreen_requested_at)}{r.req_first ? ' · ' + r.req_first : ''}</span>
        </div>)}
        {!expanded && d.count > 3 && <div style={{ fontSize: 11.5, color: '#d97706', fontWeight: 700 }}>+{d.count - 3} more — tap</div>}
        {expanded && d.count === 0 && <div style={{ ...s.sub, padding: 10 }}>No prescreens waiting 🎉</div>}
      </>);
      case 'missed_calls': return (<>
        <div style={{ fontSize: 24, fontWeight: 800, color: d.count > 0 ? '#dc2626' : '#16a34a' }}>{d.count}<span style={{ fontSize: 12, color: '#94a3b8' }}> need callback</span></div>
        {d.rows.slice(0, expanded ? 999 : 3).map(r => <div key={r.id} style={{ ...s.row, padding: expanded ? '6px 0' : '3px 0', fontSize: 12.5, alignItems: 'center' }}>
          <span>
            <span style={{ fontWeight: 700, color: '#b91c1c' }}>{r.known_name || r.caller_name || r.caller_phone}</span>
            <span style={{ ...s.sub, marginLeft: 6 }}>{new Date(r.call_started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{r.source ? ' · ' + r.source : ''}</span>
          </span>
          {expanded && (
            <button disabled={dialBusy === r.id} onClick={(e) => { e.stopPropagation(); callBack(r); }}
              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {dialBusy === r.id ? 'Dialing…' : '📞 Call Back'}
            </button>
          )}
        </div>)}
        {!expanded && d.count > 3 && <div style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700 }}>+{d.count - 3} more — tap</div>}
        {expanded && d.count === 0 && <div style={{ ...s.sub, padding: 10 }}>All missed calls handled 🎉</div>}
      </>);
      case 'pending_admits': return (<>
        <div style={{ fontSize: 24, fontWeight: 800 }}>{d.count}<span style={{ fontSize: 12, color: '#94a3b8' }}> pending</span></div>
        {(d.rows || []).slice(0, expanded ? 999 : 3).map(r => <div key={r.id} style={{ ...s.row, padding: '3px 0', fontSize: 12.5 }}>
          <span style={{ fontWeight: 600 }}>{r.patient_name}</span>
          <span style={s.sub}>{r.facility || '—'} · {r.expected_admit_date ? dt(r.expected_admit_date) : 'no date'}</span>
        </div>)}
        {!expanded && (d.rows || []).length > 3 && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>+{d.rows.length - 3} more — tap</div>}
      </>);
      case 'vobs_outstanding': return (<>
        <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
          <span style={{ fontSize: 24, fontWeight: 800 }}>{d.open}<span style={{ fontSize: 12, color: '#94a3b8' }}> open</span></span>
          <span style={{ fontSize: 24, fontWeight: 800, color: d.stuck > 0 ? '#dc2626' : '#16a34a' }}>{d.stuck}<span style={{ fontSize: 12, color: '#94a3b8' }}> stuck</span></span>
        </div>
        {(d.rows || []).slice(0, expanded ? 999 : 3).map(r => <div key={r.id} style={{ ...s.row, padding: '3px 0', fontSize: 12.5 }}>
          <span style={{ fontWeight: 600, color: r.hours_out > 24 ? '#b91c1c' : '#0f172a' }}>{r.patient_name || 'Unknown'}</span>
          <span style={s.sub}>{r.payer_name || '—'} · {r.hours_out}h · {r.rep_first || '—'}</span>
        </div>)}
        {!expanded && (d.rows || []).length > 3 && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>+{d.rows.length - 3} more — tap</div>}
      </>);
      case 'my_queue': return (<>
        <div style={{ fontSize: 28, fontWeight: 800, color: d.due > 0 ? '#336090' : '#16a34a' }}>{d.due}</div>
        <div style={s.sub}>calls to work — tap to open your queue</div>
      </>);
      case 'unclaimed_leads': return (<>
        <div style={{ fontSize: 28, fontWeight: 800, color: d.count > 0 ? '#dc2626' : '#16a34a' }}>{d.count}</div>
        <div style={{ ...s.sub, fontWeight: d.count > 0 ? 700 : 400, color: d.count > 0 ? '#b91c1c' : '#64748b' }}>{d.count > 0 ? 'first to call gets it — tap' : 'all claimed'}</div>
      </>);
      case 'log_referral': return (<>
        <div style={{ fontSize: 30 }}>➕</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#336090' }}>Tap to log a referral & create the lead</div>
      </>);

      case 'jts_admits': return (<><div style={s.big}>{d.admits}</div><div style={s.sub}>Journey to Solutions · {d.aca} ACA in range</div></>);
      case 'aca_totals': return (<><div style={{ ...s.big, color: d.total > 0 ? '#dc2626' : '#16a34a' }}>{d.total}</div>
        {d.by_facility.map(r => <div key={r.facility} style={s.row}><span>{r.facility}</span><b>{r.aca}</b></div>)}</>);
      case 'leaderboard': return (<>{d.rows.map(r => <div key={r.rank} style={s.row}>
        <span style={{ fontWeight: r.rank === 1 ? 800 : 600 }}>{r.rank === 1 ? '🏆 ' : r.rank + '. '}{r.rep}</span>
        <span><b style={{ color: scoreColor(r.score) }}>{r.score}</b>{r.admits != null ? <span style={s.sub}> · {r.admits} admits</span> : null}</span></div>)}</>);
      case 'leads_action': return (<><div style={{ ...s.big, color: d.overdue > 0 ? '#dc2626' : '#16a34a' }}>{d.overdue}</div>
        <div style={s.sub}>overdue{d.unclaimed != null ? ` · ${d.unclaimed} unclaimed` : ''}</div></>);
      case 'callbacks_today': return d.rows.length === 0 ? <div style={s.sub}>None scheduled.</div>
        : d.rows.map(r => <div key={r.id} style={s.row}><span style={{ fontWeight: 600 }}>{r.name}</span>
          <span style={s.sub}>{new Date(r.callback_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span></div>);

      case 'lead_sources': return d.rows.map(r => <div key={r.source} style={s.row}><span>{r.source}</span><b>{r.admits}</b></div>);
      case 'census_by_facility': {
        const max = Math.max(...d.rows.map(r => r.census), 1);
        return d.rows.map(r => <div key={r.facility} style={{ marginBottom: 7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}><span>{r.facility}</span><b>{r.census}</b></div>
          <div style={{ background: '#f1f5f9', borderRadius: 5, height: 7 }}><div style={{ width: (100 * r.census / max) + '%', background: '#336090', height: 7, borderRadius: 5 }} /></div></div>);
      }
      case 'open_beds': return d.rows.map(r => <div key={r.facility} style={s.row}><span>{r.facility}</span>
        <b style={{ color: r.open <= 2 ? '#dc2626' : '#16a34a' }}>{r.open} open</b></div>);
      case 'discharges_upcoming': return d.rows.length === 0 ? <div style={s.sub}>None expected.</div>
        : d.rows.map((r, i) => <div key={i} style={s.row}><span style={{ fontWeight: 600 }}>{r.patient_name}</span><span style={s.sub}>{dt(r.expected_dc)} · {r.facility}</span></div>);
      case 'ur_expiring': return d.rows.length === 0 ? <div style={s.sub}>Nothing expiring.</div>
        : d.rows.map((r, i) => <div key={i} style={s.row}><span style={{ fontWeight: 600 }}>{r.patient_name}</span>
          <span style={{ color: '#dc2626', fontWeight: 700 }}>{dt(r.auth_thru)}</span></div>);
      case 'collections_range': return (<>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a' }}>{money(d.collected)}</div>
        <div style={s.sub}>{d.payments || 0} payments received — tap for full report</div>
      </>);
      case 'forecast_week': return <div style={s.big}>{money(d.predicted)}</div>;
      case 'ai_review_pending': return <div style={{ ...s.big, color: d.count > 0 ? '#d97706' : '#16a34a' }}>{d.count}</div>;
      case 'tickets_open': return (<>
        <div style={{ fontSize: 28, fontWeight: 800, color: d.count > 0 ? '#d97706' : '#16a34a' }}>{d.count}</div>
        <div style={s.sub}>{d.all ? 'open team tickets — tap' : 'my open tickets — tap'}</div>
      </>);
      default: return <div style={s.sub}>—</div>;
    }
  };
  return (
    <div style={expanded ? { background: '#fff' } : s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {!expanded && <div style={s.title}>{TITLES[k] || k}</div>}
        {editMode && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button style={s.ghost} onClick={() => onMove(k, -1)}>◀</button>
            <button style={s.ghost} onClick={() => onMove(k, 1)}>▶</button>
            <button style={{ ...s.ghost, color: '#b91c1c' }} onClick={() => onRemove(k)}>✕</button>
          </div>
        )}
      </div>
      {body()}
    </div>
  );
}

export default function CrmDashboard({ user, nav }) {
  const [phoneOpen, setPhoneOpen] = useState(false);
  const headers = { Authorization: `Bearer ${user.token}` };
  const [d, setD] = useState(null);
  const [range, setRange] = useState('mtd');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [editMode, setEditMode] = useState(false);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );
  const [showAdd, setShowAdd] = useState(false);
  const [facility, setFacility] = useState('');
  const [refModal, setRefModal] = useState(false);
  const [refPartners, setRefPartners] = useState([]);
  const hasMessenger = !!(d && d.widgets && d.widgets.messenger);
  useEffect(() => {
    if (!hasMessenger) return;
    let dead = false;
    const load = async () => {
      try {
        const r = await fetch(API + '/api/msg/threads', { headers });
        const rows = await r.json();
        if (!dead && Array.isArray(rows)) setD(prev => prev && prev.widgets && prev.widgets.messenger
          ? { ...prev, widgets: { ...prev.widgets, messenger: { ...prev.widgets.messenger, threads: rows } } } : prev);
      } catch (e) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { dead = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMessenger]);
  const [refForm, setRefForm] = useState({ partner: '', first: '', last: '', phone: '' });
  const [refBusy, setRefBusy] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/lookups`, { headers }).then(r => r.json()).then(j => {
      if (!j.ok) return;
      const SKIP = ['Direct','Google Ads','Google My Business','Google Organic','Main Line','Meta Ads','Organic','Other','Website','Referral Partner'];
      const sources = (j.lead_sources || []).filter(x => !SKIP.includes(x.name))
        .sort((a, b) => (/journey/i.test(a.name) ? -1 : /journey/i.test(b.name) ? 1 : a.name.localeCompare(b.name)))
        .map(x => ({ ...x, kind: 'source' }));
      const partners = (j.referral_partners || []).map(x => ({ ...x, kind: 'partner' }));
      setRefPartners([...sources, ...partners]);
      if (sources[0]) setRefForm(f => ({ ...f, partner: sources[0].id }));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const [dialBusy, setDialBusy] = useState(null);
  const callBack = async (row) => {
    setDialBusy(row.id);
    try {
      const r = await fetch(`${API}/api/call/dial`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: row.lead_id || null, phone: row.caller_phone }),
      });
      const j = await r.json();
      if (j.ok) { window.alert('Calling you now — answer and CallRail connects you to ' + (row.known_name || row.caller_name || row.caller_phone)); setTimeout(load, 4000); }
      else window.alert(j.message || j.error || 'Dial failed');
    } catch (e) { window.alert('Network error'); }
    setDialBusy(null);
  };
  const submitReferral = async (thenCall) => {
    setRefBusy(true);
    try {
      const p = refPartners.find(x => x.id === refForm.partner);
      const body = { first_name: refForm.first, last_name: refForm.last, phone: refForm.phone, partner_name: p ? p.name : null };
      if (p && p.kind === 'source') body.lead_source_id = p.id; else body.referral_partner_id = refForm.partner || null;
      const r = await fetch(`${API}/api/referrals/log-and-create`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (j.ok) {
        const ph = refForm.phone;
        setRefModal(false);
        setRefForm(f => ({ ...f, first: '', last: '', phone: '' }));
        if (thenCall) {
          const dr = await fetch(`${API}/api/call/dial`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ lead_id: j.lead_id, phone: ph }) });
          const dj = await dr.json();
          if (dj.ok) window.alert('Lead created — calling you now. Answer and CallRail connects you.');
          else window.alert('Lead created, but the call failed: ' + (dj.message || dj.error || ''));
        }
        load();
      } else alert('Failed: ' + j.error);
    } catch (e) { alert(e.message); }
    setRefBusy(false);
  };
  const [report, setReport] = useState(null); // { kind, title, rows, from, to }

  const [expandWidget, setExpandWidget] = useState(null);
  const [scope, setScope] = useState('all'); // all | my_day | my_team | organization
  const REPORT_FOR = { team_admits_gauge: 'admits', jts_admits: 'jts_admits', aca_totals: 'aca', collections_range: 'collections', clinical_safety: 'clinical_findings', ready_to_bill: 'unbilled', ur_expiring_dollars: 'ur_risk_expiring', money_ar: 'ar_open' };
  const NAV_FOR = { my_queue: 'queue', unclaimed_leads: 'queue', ai_review_pending: 'calls', tickets_open: 'tickets' };
  const openReport = async (widgetKey) => {
    if (widgetKey === 'log_referral') { setRefModal(true); return; }
    if (NAV_FOR[widgetKey] && nav) { nav(NAV_FOR[widgetKey]); return; }
    const kind = REPORT_FOR[widgetKey];
    if (!kind) {
      if (widgetKey === 'aca_totals') { /* aca gets report if permitted, else expand */ }
      setExpandWidget(widgetKey);
      return;
    }
    const { from, to } = rangeDates();
    try {
      const r = await fetch(`${API}/api/reports/${kind}?from=${from}&to=${to}`, { headers });
      const j = await r.json();
      if (j.ok) setReport({ kind, title: TITLES[widgetKey], rows: j.rows, from: j.from, to: j.to });
      else setExpandWidget(widgetKey);
    } catch (e) { console.error(e); }
  };
  const reloadReport = async (from, to) => {
    if (!report) return;
    try {
      const r = await fetch(`${API}/api/reports/${report.kind}?from=${from}&to=${to}`, { headers });
      const j = await r.json();
      if (j.ok) setReport({ ...report, rows: j.rows, from: j.from || from, to: j.to || to });
    } catch (e) { console.error(e); }
  };
  const presetRange = (days) => {
    const today = new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }));
    const iso = (d) => d.toISOString().slice(0, 10);
    if (days === 'mtd') return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    return { from: iso(new Date(today.getTime() - days * 86400000)), to: iso(today) };
  };

  const downloadCsv = () => {
    if (!report) return;
    window.open(`${API}/api/reports/${report.kind}?from=${report.from}&to=${report.to}&format=csv&token=`, '_self');
  };
  const csvViaFetch = async () => {
    if (!report) return;
    const r = await fetch(`${API}/api/reports/${report.kind}?from=${report.from}&to=${report.to}&format=csv`, { headers });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${report.kind}_${report.from}_${report.to}.csv`;
    a.click();
  };
  const printPdf = () => {
    if (!report) return;
    const cols = report.rows.length ? Object.keys(report.rows[0]) : [];
    const doc = new jsPDF({ orientation: cols.length > 6 ? 'landscape' : 'portrait' });
    doc.setFontSize(15); doc.setTextColor(26, 74, 107);
    doc.text(report.title, 14, 16);
    doc.setFontSize(10); doc.setTextColor(100, 116, 139);
    doc.text(`${report.from} to ${report.to} — ${report.rows.length} rows — generated ${new Date().toLocaleString()}`, 14, 22);
    autoTable(doc, {
      startY: 27,
      head: [cols.map(c => c.replace(/_/g, ' ').toUpperCase())],
      body: report.rows.map(r => cols.map(c => {
        const v = r[c];
        if (v == null) return '';
        const sv = String(v);
        return /^\d{4}-\d{2}-\d{2}T/.test(sv) ? sv.slice(0, 10) : sv.slice(0, 60);
      })),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [26, 74, 107], fontSize: 7.5 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`${report.kind}_${report.from}_${report.to}.pdf`);
  };

  const rangeDates = useCallback(() => {
    const now = new Date();
    const iso = (x) => x.toISOString().slice(0, 10);
    if (range === 'mtd') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
    if (range === 'last_month') {
      return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
    }
    if (range === '90') { const f = new Date(now.getTime() - 90 * 86400000); return { from: iso(f), to: iso(now) }; }
    if (range === 'ytd') return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
    if (range === 'custom' && custom.from && custom.to) return custom;
    return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  }, [range, custom]);

  const load = useCallback(async () => {
    const { from, to } = rangeDates();
    try {
      const r = await fetch(`${API}/api/dashboard/v2?from=${from}&to=${to}${facility ? '&facility=' + facility : ''}`, { headers });
      const j = await r.json();
      if (j.ok) setD(j);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line
  }, [rangeDates, facility]);
  useEffect(() => { load(); }, [load]);

  const saveLayout = async (layout) => {
    setD(prev => ({ ...prev, layout }));
    await fetch(`${API}/api/dashboard/v2/layout`, { method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ layout }) });
  };
  const remove = (k) => saveLayout(d.layout.filter(x => x !== k));
  const move = (k, dir) => {
    const i = d.layout.indexOf(k), j = i + dir;
    if (j < 0 || j >= d.layout.length) return;
    const L = [...d.layout]; [L[i], L[j]] = [L[j], L[i]];
    saveLayout(L);
  };
  const add = (k) => { saveLayout([...d.layout, k]); setShowAdd(false); };

  useEffect(() => {
    document.body.style.overflow = (report || expandWidget) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [report, expandWidget]);

  if (!d) return <div style={s.wrap}><div style={{ color: '#94a3b8', padding: 30 }}>Loading…</div></div>;
  const addable = d.available.filter(w => !d.layout.includes(w));

  return (
    <div style={s.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={s.h1}>Home</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {d && d.widget_scopes && (
            <span style={{ display: 'inline-flex', gap: 4, marginRight: 10 }}>
              {[['all','All'],['my_day','My Day'],['my_team','My Team'],['organization','Organization']].map(([k2, l2]) => (
                <button key={k2} style={s.rangeBtn(scope === k2)} onClick={() => setScope(k2)}>{l2}</button>
              ))}
            </span>
          )}
          {editMode && addable.length > 0 && <button style={{ ...s.rangeBtn(true) }} onClick={() => setShowAdd(!showAdd)}>+ Add widget</button>}
          <button style={s.rangeBtn(editMode)} onClick={() => { setEditMode(!editMode); setShowAdd(false); }}>{editMode ? 'Done' : 'Customize'}</button>
        </div>
      </div>

      <div style={s.bar}>
        {[['mtd','This month'],['last_month','Last month'],['90','Last 90 days'],['ytd','Year to date'],['custom','Custom']].map(([k, l]) =>
          <button key={k} style={s.rangeBtn(range === k)} onClick={() => setRange(k)}>{l}</button>)}
        {range === 'custom' && (<>
          <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13 }} />
          <span style={s.sub}>to</span>
          <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13 }} />
        </>)}
        <select value={facility} onChange={e => setFacility(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, color: '#334155', background: '#fff' }}>
          <option value="">All Centers</option>
          {(d.facilities || []).map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
        </select>
        <span style={{ ...s.sub, marginLeft: 'auto' }}>{dt(d.from)} – {dt(d.to)}</span>
      </div>

      {showAdd && (
        <div style={{ ...s.card, marginBottom: 14 }}>
          <div style={s.title}>Add a widget</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {addable.map(w => <button key={w} style={s.ghost} onClick={() => add(w)}>+ {TITLES[w] || w}</button>)}
          </div>
        </div>
      )}

      <DndContext sensors={dndSensors} collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;
          const from = d.layout.indexOf(active.id), to = d.layout.indexOf(over.id);
          if (from < 0 || to < 0) return;
          saveLayout(arrayMove(d.layout, from, to));
        }}>
        <SortableContext items={d.layout} strategy={rectSortingStrategy}>
          <div style={s.grid}>
            {d.layout.filter(k => editMode || scope === 'all' || !d.widget_scopes
              || (Array.isArray(d.widget_scopes[k]) && d.widget_scopes[k].includes(scope))).map(k => (
              <SortableTile key={k} id={k} editMode={editMode} onClick={() => !editMode && (k === 'messenger' ? setPhoneOpen(true) : openReport(k))}>
                <Widget k={k} d={d.widgets[k]} editMode={editMode} onRemove={remove} onMove={move} expanded={false} callBack={callBack} dialBusy={dialBusy} user={user} />
              </SortableTile>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {phoneOpen && (
        <div onClick={() => setPhoneOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.5)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}>
            <PhoneMessenger user={user} />
          </div>
        </div>
      )}

      {refModal && (
        <div onClick={() => setRefModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 420 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#336090', marginBottom: 4 }}>Log a Referral</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>Creates the lead instantly — work it from your queue.</div>
            <select value={refForm.partner} onChange={e => setRefForm({ ...refForm, partner: e.target.value })}
              style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 10 }}>
              <optgroup label="Referral Sources">
                {refPartners.filter(p => p.kind === 'source').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
              <optgroup label="Treatment Center Partners">
                {refPartners.filter(p => p.kind === 'partner').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            </select>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input placeholder="First name" value={refForm.first} onChange={e => setRefForm({ ...refForm, first: e.target.value })}
                style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} />
              <input placeholder="Last name" value={refForm.last} onChange={e => setRefForm({ ...refForm, last: e.target.value })}
                style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }} />
            </div>
            <input placeholder="Phone" value={refForm.phone} onChange={e => setRefForm({ ...refForm, phone: e.target.value })}
              style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setRefModal(false)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button disabled={refBusy || !refForm.first.trim() || !refForm.phone.trim()} onClick={() => submitReferral(false)}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #336090', background: '#fff', color: '#336090', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Create Lead</button>
              <button disabled={refBusy || !refForm.first.trim() || !refForm.phone.trim()} onClick={() => submitReferral(true)}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>📞 Create & Call</button>
            </div>
          </div>
        </div>
      )}

      {expandWidget && (
        <div onClick={() => setExpandWidget(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(560px, 94vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontWeight: 800, fontSize: 16 }}>{TITLES[expandWidget]}</span>
              <button onClick={() => setExpandWidget(null)} style={{ ...s.ghost, fontWeight: 800 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '12px 18px 18px' }}>
              <Widget k={expandWidget} d={d.widgets[expandWidget]} editMode={false} onRemove={() => {}} onMove={() => {}} expanded={true} nav={nav} callBack={callBack} dialBusy={dialBusy} />
            </div>
          </div>
        </div>
      )}

      {report && (
        <div onClick={() => setReport(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: 'min(1000px, 96vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #e2e8f0' }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: 16 }}>{report.title}</span>
                <span style={{ ...s.sub, marginLeft: 10 }}>{report.from} – {report.to} · {report.rows.length} rows</span>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[['mtd', 'This month'], [30, '30d'], [60, '60d'], [90, '90d']].map(([v, lbl]) => {
                    const r = presetRange(v);
                    const active = report.from === r.from && report.to === r.to;
                    return <button key={String(v)} onClick={() => reloadReport(r.from, r.to)}
                      style={{ padding: '3px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                        border: '1px solid ' + (active ? '#2563eb' : '#e2e8f0'),
                        background: active ? '#2563eb' : '#fff', color: active ? '#fff' : '#334155' }}>{lbl}</button>;
                  })}
                  <input type="date" defaultValue={report.from} id="rep-from"
                    style={{ padding: '2px 6px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 11.5 }} />
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>to</span>
                  <input type="date" defaultValue={report.to} id="rep-to"
                    style={{ padding: '2px 6px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 11.5 }} />
                  <button onClick={() => { const f = document.getElementById('rep-from').value, t = document.getElementById('rep-to').value; if (f && t) reloadReport(f, t); }}
                    style={{ padding: '3px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', color: '#334155' }}>Apply</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.ghost} onClick={printPdf}>Download PDF</button>
                <button style={s.ghost} onClick={csvViaFetch}>Download Spreadsheet</button>
                <button onClick={() => setReport(null)} style={{ ...s.ghost, fontWeight: 800 }}>✕</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '10px 18px 18px' }}>
              {report.rows.length === 0 ? <div style={{ ...s.sub, padding: 20 }}>No rows in this range.</div> : (
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
                  <thead><tr>{Object.keys(report.rows[0]).map(c =>
                    <th key={c} style={{ textAlign: 'left', padding: '6px 8px', background: '#f1f5f9', textTransform: 'uppercase', fontSize: 10.5, color: '#64748b', position: 'sticky', top: 0 }}>{c.replace(/_/g, ' ')}</th>)}</tr></thead>
                  <tbody>{report.rows.map((r, i) =>
                    <tr key={i}>{Object.keys(report.rows[0]).map(c =>
                      <td key={c} style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}>{r[c] == null ? '—' : String(r[c]).slice(0, 10) === String(r[c]) || !/^\d{4}-\d{2}-\d{2}T/.test(String(r[c])) ? String(r[c]) : String(r[c]).slice(0, 10)}</td>)}</tr>)}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
